<template>
  <div>
    <v-data-table
      :footer-props="footerProps"
      :headers="headerList"
      :items="filteredReleaseNoteList"
      :loading="loading"
      :options.sync="tableOptions"
      :server-items-length="isGlobalSearch ? searchTotalCount : undefined"
      :single-select="false"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      @update:options="handlePagination"
      class="elevation-0 och-dataTable--fixedHeader"
      data-cy="listTable"
      fixed-header
      height="60vh"
      item-key="releaseNoteId"
      no-data-text="No Release Notes match the current filter."
    >
      <template v-slot:header.batchEdit>
        <v-checkbox
          :disabled="!searchReleaseNoteList || !searchReleaseNoteList.length"
          :indeterminate="isIndeterminate"
          :value="isAllSelected"
          @change="selectAll($event)"
          class="mt-2"
          data-cy="releaseNoteTable__selectAll"
        ></v-checkbox>
      </template>
      <template v-slot:item="{ item }">
        <tr data-cy="releaseNoteTable__row" :class="getRowStyles(item)">
          <template>
            <td>
              <v-checkbox
                :data-cy="`releaseNoteTable-select-${item.id}`"
                :value="!!releaseNoteSelectionListLookup[item.id]"
                @change="select(item, $event)"
              ></v-checkbox>
            </td>
          </template>
          <td>
            <v-btn icon small @click="edit(item)">
              <v-icon small color="grey darken-2">fas fa-edit</v-icon>
            </v-btn>
          </td>
          <td>{{ item.datePublished }}</td>
          <td v-if="isGlobalSearch">{{ item.packTitle }} ({{ item.packId }})</td>
          <td>{{ item.id }}</td>
          <td>
            <router-link
              :to="{
                name: isOchinUser ? 'releaseNoteView' : 'testPlanView',
                params: {
                  packId: item.packId,
                  releaseNoteId: isOchinUser ? item.id : item.releaseNoteId,
                  serviceAreaId: `${currentServiceArea}`,
                },
                query: { globalSearch: isGlobalSearch ? '1' : '' },
              }"
              >{{ item.title }}</router-link
            >
          </td>
          <td>{{ item.documentationStatus }}</td>
          <td>
            <v-tooltip v-if="item.summary" top max-width="420">
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">{{ truncate(stripHtmlTags(item.summary)) }}</div>
              </template>
              <span v-html="item.summary"></span>
            </v-tooltip>
          </td>
          <td>{{ item.buildType }}</td>
          <td>{{ item.application }}</td>
          <td>{{ item.primaryActivity }}</td>
          <td>{{ formatArray(item.userRole) }}</td>
          <td>{{ item.releaseType }}</td>
          <td>{{ item.operationalizeType }}</td>
          <td>{{ item.qaAnalystDisplayName || "--" }}</td>
          <td>{{ item.qaJira }}</td>
          <td>{{ item.buildAnalystDisplayName || "--" }}</td>
        </tr>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { formatArray } from "@/lib/formatters";
import { truncate, stripHtmlTags } from "@/lib/summaryFormatters";
import { mapState, mapMutations } from "vuex";
import { cloneDeep, keyBy } from "lodash";

export default {
  computed: {
    filteredReleaseNoteList() {
      if (!this.quickSearchTerms || this.isGlobalSearch) {
        return this.searchReleaseNoteList;
      }
      const filteredList = this.searchReleaseNoteList.filter(item => {
        const searchTerms = this.quickSearchTerms.toLowerCase().split(" ");
        const searchableFields = this.headers.map(header => header.value);

        return searchableFields.some(field => {
          const fieldValue = item[field] ? item[field].toString().toLowerCase() : "";
          return searchTerms.every(term => fieldValue.includes(term));
        });
      });
      return filteredList;
    },
    headerList() {
      const { globalHeaders, isGlobalSearch, headers } = this;
      const clonedHeaderList = cloneDeep(headers);
      if (isGlobalSearch) {
        clonedHeaderList.splice(2, 0, ...globalHeaders);
      }
      return clonedHeaderList;
    },
    tableOptions: {
      get() {
        return this.options;
      },
      set(val) {
        this.updateOptions(val);
      },
    },
    releaseNoteSelectionListLookup() {
      return keyBy(this.releaseNoteSelectionList, "id");
    },
    ...mapState({
      currentServiceArea: state => state.session.serviceArea,
      isOchinUser: state => state.session.user.isOchinUser,
      searchTotalCount: state => state.releaseNote.searchTotalCount,
      releaseNoteSelectionList: state => state.releaseNote.releaseNoteBatchEditSelectionList,
    }),
  },
  mounted() {
    this.sortBy = "productionDate";
    this.sortDesc = true;
    // The store needs to be reset so that styles reset properly when navigating back to this view.
    this.updateReleaseNoteBatchEditSelectionList([]);
  },
  data() {
    return {
      activeTab: null,
      dataIsLoaded: false,
      footerProps: {
        "items-per-page-options": [5, 10, 25, 50],
      },
      formatArray,
      globalHeaders: [
        {
          text: "Pack",
          value: "packId",
          width: "150",
        },
      ],
      isAllSelected: false,
      isIndeterminate: false,
      headers: [
        {
          text: "",
          value: "batchEdit",
          sortable: false,
        },
        {
          text: "",
          value: "vue_editHeader",
        },
        {
          text: "Date Published",
          value: "datePublished",
          width: "130",
        },
        {
          text: "ID",
          value: "id",
          width: "75",
        },
        {
          text: "Title",
          value: "title",
          width: "275",
        },
        {
          text: "Documentation Status",
          value: "documentationStatus",
          width: "150",
        },
        {
          text: "Summary",
          value: "summary",
          width: "300",
        },
        {
          text: "Build Type",
          value: "buildType",
          width: "50",
        },
        {
          text: "Application",
          value: "application",
          width: "130",
        },
        {
          text: "Primary Activity",
          value: "primaryActivity",
          width: "180",
        },
        {
          text: "User Role",
          value: "userRole",
          width: "130",
        },
        {
          text: "Release Type",
          value: "releaseType",
          width: "130",
        },
        {
          text: "Operationalize Type",
          value: "operationalizeType",
        },
        {
          text: "QA Analyst",
          value: "qaAnalyst",
          width: "130",
        },
        {
          text: "QA JIRA",
          value: "qaJira",
          width: "130",
        },
        {
          text: "Build Analyst",
          value: "buildAnalyst",
          width: "130",
        },
      ],
      sortBy: [],
      sortDesc: null,
    };
  },
  methods: {
    edit(releaseNote) {
      const { id: releaseNoteId, packId } = releaseNote;
      this.$router.push({ name: "releaseNoteEdit", params: { releaseNoteId, packId } });
    },
    truncate,
    getRowStyles(item) {
      if (!!this.releaseNoteSelectionListLookup[item.id]) {
        return "och-releaseNoteTable__row--highlighted";
      }
    },
    handlePagination(val) {
      this.$emit("paginate", val);
    },
    select(item, value) {
      const { id } = item;
      if (!id) {
        return;
      }

      let selectionList = cloneDeep(this.releaseNoteSelectionList);
      if (value) {
        selectionList.push(item);
      } else {
        selectionList = this.releaseNoteSelectionList.filter(releaseNote => releaseNote.id !== id);
      }
      if (selectionList.length > 0) {
        this.isIndeterminate = true;
        this.isAllSelected = false;
      }
      if (selectionList.length === 0) this.isIndeterminate = false;
      this.updateReleaseNoteBatchEditSelectionList(selectionList);
    },
    selectAll(value) {
      if (value) {
        this.updateReleaseNoteBatchEditSelectionList(cloneDeep(this.searchReleaseNoteList));
        this.isAllSelected = true;
      } else {
        this.updateReleaseNoteBatchEditSelectionList([]);
        this.isAllSelected = false;
      }
      this.isIndeterminate = false;
    },

    stripHtmlTags,
    updateOptions(val) {
      this.$emit("updateOptions", val);
    },
    ...mapMutations({
      updateReleaseNoteBatchEditSelectionList: "releaseNote/setReleaseNoteBatchEditSelectionList",
    }),
  },
  props: {
    isGlobalSearch: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Object,
      required: true,
    },
    quickSearchTerms: {
      type: String,
      default: "",
    },
    searchReleaseNoteList: {
      type: Array,
      required: true,
    },
  },
  watch: {
    searchReleaseNoteList() {
      if (!this.searchReleaseNoteList || this.searchReleaseNoteList.length === 0) {
        this.isAllSelected = false;
        this.isIndeterminate = false;
      }
    },
    releaseNoteSelectionList() {
      if (!this.releaseNoteSelectionList || this.releaseNoteSelectionList.length === 0) {
        this.isAllSelected = false;
        this.isIndeterminate = false;
      }
    },
  },
};
</script>

<style>
/* Styles are intentionally not scoped using the scoped keyword due to a strange bug
that results in the first table row not highlighting when selected if styles are scoped. */

.och-releaseNoteTable__row--highlighted {
  background-color: #ffffbf;
}

.och-releaseNoteTable__row--highlighted:hover {
  background-color: #ebebb7 !important;
}
</style>
